import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import openai from '../../../utils/openaiClient';

const AITextGenerator = ({ onInsertText, textComponents }) => {
  const [prompt, setPrompt] = useState('');
  const [generatedText, setGeneratedText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState('');
  const [textType, setTextType] = useState('summary');

  const textTypes = [
    { id: 'summary', label: 'Executive Summary', icon: 'FileText' },
    { id: 'narrative', label: 'Compliance Narrative', icon: 'AlignLeft' },
    { id: 'recommendations', label: 'Recommendations', icon: 'Lightbulb' },
    { id: 'findings', label: 'Key Findings', icon: 'Search' },
  ];

  const handleGenerateText = async () => {
    if (!prompt?.trim()) return;

    setIsGenerating(true);
    try {
      const systemPrompt = {
        summary: 'You are an expert at writing executive summaries for safety reports. Create concise, professional summaries that highlight key points and actionable insights.',
        narrative: 'You are a compliance expert. Write clear, professional compliance narratives that explain regulatory requirements, current status, and necessary actions.',
        recommendations: 'You are a safety consultant. Provide specific, actionable recommendations based on the provided data and context.',
        findings: 'You are a data analyst. Summarize key findings from safety data in a clear, structured format with supporting evidence.',
      };

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { role: 'system', content: systemPrompt?.[textType] },
          { role: 'user', content: prompt },
        ],
        reasoning_effort: 'low',
        verbosity: 'medium',
        max_completion_tokens: 1000,
      });

      const text = response?.choices?.[0]?.message?.content;
      setGeneratedText(text);
    } catch (error) {
      console.error('Error generating text:', error);
      setGeneratedText('Error generating text. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleInsert = () => {
    if (selectedComponent && generatedText) {
      onInsertText?.(generatedText, selectedComponent);
      setGeneratedText('');
      setPrompt('');
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Sparkles" size={24} color="var(--color-primary)" />
        <h2 className="text-xl font-semibold text-foreground">AI Text Generator</h2>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Content Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {textTypes?.map((type) => (
              <button
                key={type?.id}
                onClick={() => setTextType(type?.id)}
                className={`flex items-center gap-2 p-2 rounded-lg text-sm font-medium transition-colors ${
                  textType === type?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name={type?.icon} size={14} />
                {type?.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Describe what you need
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e?.target?.value)}
            placeholder="E.g., Create an executive summary for quarterly safety performance highlighting incident reduction, training improvements, and key safety initiatives..."
            className="w-full h-32 px-4 py-2 border border-border rounded-lg bg-background text-foreground resize-none"
          />
        </div>

        <button
          onClick={handleGenerateText}
          disabled={!prompt?.trim() || isGenerating}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating ? (
            <>
              <Icon name="Loader" size={16} className="animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Icon name="Sparkles" size={16} />
              Generate Text
            </>
          )}
        </button>

        {generatedText && (
          <div className="space-y-3">
            <div className="p-4 bg-background border border-border rounded-lg">
              <p className="text-sm text-foreground whitespace-pre-wrap">{generatedText}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Insert into component
              </label>
              <select
                value={selectedComponent}
                onChange={(e) => setSelectedComponent(e?.target?.value)}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="">Select text component...</option>
                {textComponents?.map((component) => (
                  <option key={component?.instanceId} value={component?.instanceId}>
                    {component?.name} ({component?.position + 1})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleInsert}
                disabled={!selectedComponent}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="Plus" size={16} />
                Insert Text
              </button>
              <button
                onClick={() => {
                  setGeneratedText('');
                  setPrompt('');
                }}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm font-medium"
              >
                <Icon name="RefreshCw" size={16} />
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AITextGenerator;